import { useQuery } from "@tanstack/react-query";
import { rxKeys } from "./rx.keys";
import { useApi } from "../../api/ApiProvider";
import { RxDeletionCheckDto, RxDto, RxIdDto, RxSeriesDto, RxLightDto, RxTableFilters, RxTableDto, TemplateWidgetDataDto } from "../../api-client";
import { eventNames } from "process";

/**
 * Hook to fetch a single RX by ID
 * Replaces the need for rowData prop in RxRowOverview
 */
export const useRx = (rxId: string) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxDto>({
    queryKey: rxKeys.rx.detail(rxId),
    queryFn: async () => {
      const response = await rxApi.getRxWithValidations(rxId);
      return response;
    },
    enabled: !!rxId,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter than IDs since this is more detailed data)
    meta: { errorMessage: "Rezept konnte nicht geladen werden" },
  });
};

/**
 * Pre-flight check: whether an Rx may be (soft-)deleted. Returns the list of
 * blockers (patient bill, attended appointments, insurance bill) so the UI can
 * explain why deletion is not possible. Disabled until `enabled` is true so the
 * check only runs when the user is about to delete.
 */
export const useRxDeletable = (rxId: string | undefined, enabled: boolean) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxDeletionCheckDto>({
    queryKey: rxKeys.rxDeletable.detail(rxId ?? ""),
    queryFn: async () => await rxApi.checkRxDeletable(rxId!),
    enabled: !!rxId && enabled,
    staleTime: 0,
    meta: { errorMessage: "Löschprüfung konnte nicht durchgeführt werden" },
  });
};

export const useRxLackingAppointments = (patientId: number) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxDto[]>({
    queryKey: rxKeys.rxLackingAppointments.forPatient(patientId),
    queryFn: async () => {
      const response = await rxApi.getRxLackingAppointments(patientId);
      return response;
    },
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter than IDs since this is more detailed data)
    meta: { errorMessage: "Rezept konnte nicht geladen werden" },
  });
};

/**
 * Generic bootstrap data for the dynamic template widget: all selectable
 * (non-BOOKED) Rxs for the patient plus an optional default selection. Only
 * fetched when the widget is summoned (pass enabled=false to skip).
 */
export const useTemplateWidgetData = (patientId: number, enabled: boolean = true) => {
  const { rx: rxApi } = useApi();
  return useQuery<TemplateWidgetDataDto>({
    queryKey: rxKeys.templateWidgetData.forPatient(patientId),
    queryFn: async () => await rxApi.getTemplateWidgetData(patientId),
    enabled: !!patientId && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    meta: { errorMessage: "Vorlagendaten konnten nicht geladen werden" },
  });
};


/**
 * Used by the calendar to define the Rx tabs under the Patient Overview
 * @param patientId 
 * @param activeOnly 
 * @returns 
 */
export const useAllRxIds = (patientId: number, activeOnly: boolean = true) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxIdDto[]>({
    queryKey: rxKeys.rxIds.detail(patientId, activeOnly),
    queryFn: async () => {
      const response = await rxApi.getAllRxIds(patientId, activeOnly);
      return response || [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Rx IDs konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch RX series for a patient
 * Replaces RxStore.getRxSeriesForPatient
 */
export const usePatientRxSeries = (patientId: number) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxSeriesDto[]>({
    queryKey: rxKeys.rxSeriesList.forPatient(patientId),
    queryFn: async () => {
      const response = await rxApi.getRxSeriesForPatient(patientId);
      return Array.isArray(response) ? response : [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "RX-Serien konnten nicht geladen werden" },
  });
};

export const useRxSeries = (seriesId: string) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxSeriesDto>({
    queryKey: rxKeys.rxSeries.detail(seriesId),
    queryFn: async () => {
      const response = await rxApi.getRxSeriesForSeriesId(seriesId);
      return response;
    },
    enabled: !!seriesId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "RX-Serien konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch therapy RX series for a patient
 * Replaces RxStore.getTherapyRxsSeriesForPatient
 */
export const useTherapyRxSeries = (patientId: number) => {
  const { rx: rxApi } = useApi();
  return useQuery({
    queryKey: rxKeys.therapyRxSeries.forPatient(patientId),
    queryFn: async () => {
      const response = await rxApi.getTherapyRxsSeriesForPatient(patientId);
      return Array.isArray(response) ? response : [];
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Therapie RX-Serien konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch RX list for a patient using the new lightweight endpoint
 * Replaces usePatientRxs from patient.queries.ts
 */
export const useRxsForPatient = (patientId: number) => {
  const { rx: rxApi } = useApi();
  return useQuery<RxLightDto[]>({
    queryKey: rxKeys.rxList.forPatient(patientId),
    queryFn: async () => await rxApi.getRxsForPatient(patientId),
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    meta: { errorMessage: "Rezeptdaten konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch a single RxLightDto by rxId from cached patient data
 * This avoids additional API calls by leveraging cached useRxsForPatient data
 */
export const useRxLight = (rxId: string | undefined, patientId: number) => {
  const { data: rxList = [] } = useRxsForPatient(patientId);
  return rxList.find(rx => rx.id === rxId);
};

export const useAppStats=(rxIds: string[],patientId:number)=>{
  const { rx: rxApi } = useApi();
  return useQuery({
    queryKey: rxKeys.rxList.forPatient(patientId),
    queryFn: async () => {
      const response = await rxApi.getAppStats(rxIds);
      return response;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "App-Statistiken konnten nicht geladen werden" },
  });
};